import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useSnackbar } from "../context/SnackbarContext";
import { baseFetch } from "../helpers/apiClient";
import { fetchProfileInfo } from "../redux/profileSlice";

const ApplySubscriptionDiscount = ({ activePack }) => {
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const [eligibility, setEligibility] = useState(null);
  const [open, setOpen] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activePack || activePack.status === "canceled") return;
    const controller = new AbortController();

    baseFetch({
      url: "/v1/web/subscription/discount",
      method: "GET",
      signal: controller.signal,
    })
      .then((response) => {
        if (!controller.signal.aborted) {
          setEligibility({
            activePack,
            allowed: Boolean(response?.data?.canApplyDiscount),
          });
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setEligibility(null);
      });
    return () => controller.abort();
  }, [activePack]);
  const canApplyDiscount =
    activePack &&
    activePack.status !== "canceled" &&
    eligibility?.activePack === activePack &&
    eligibility.allowed;

  const handleClose = () => {
    setOpen(false);
    setCouponCode("");
    setError("");
  };

  const handleApply = async () => {
    const code = couponCode.trim();
    if (!code) {
      setError("Enter a discount code");
      return;
    }

    setLoading(true);
    setError("");
    try {
      await baseFetch({
        url: "/v1/web/subscription/discount",
        method: "POST",
        body: { couponCode: code },
      });
      setEligibility(null);
      handleClose();
      await dispatch(fetchProfileInfo());
      showSnackbar({
        message: "Discount code applied to your subscription",
        severity: "success",
      });
    } catch (message) {
      setError(message || "Unable to apply discount code");
    } finally {
      setLoading(false);
    }
  };

  if (!canApplyDiscount) return null;

  return (
    <>
      <Button
        variant="text"
        onClick={() => setOpen(true)}
        sx={{
          color: "#2A4876",
          textTransform: "none",
          textDecoration: "underline",
        }}
      >
        Apply a discount code
      </Button>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
        <DialogTitle>Apply discount code</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This code will apply to your current subscription. Only one discount
            can be active at a time.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Discount code"
            value={couponCode}
            onChange={(event) =>
              setCouponCode(event.target.value.toUpperCase())
            }
            error={Boolean(error)}
            helperText={error}
            inputProps={{ maxLength: 100 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleApply} disabled={loading}>
            Apply code
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ApplySubscriptionDiscount;
